import React from 'react';
import clsx from 'clsx';
import { ChevronDown } from '../icons/index.jsx';

export default function NavItem({
  icon: Icon,
  label,
  labelContent,
  active = false,
  children,
  collapsed = false,
  open,
  onToggle,
  submenuId,
}) {
  const childArray = React.Children.toArray(children);
  const hasChildren = childArray.length > 0;
  const [flyoutOpen, setFlyoutOpen] = React.useState(false);
  React.useEffect(() => {
    if (!collapsed) {
      setFlyoutOpen(false);
    }
  }, [collapsed]);

  const isOpen = hasChildren ? (collapsed ? flyoutOpen : Boolean(open)) : false;

  const handleToggle = () => {
    if (!hasChildren) {
      return;
    }

    if (collapsed) {
      setFlyoutOpen(prev => !prev);
      return;
    }

    onToggle?.();
  };

  const handleKeyDown = event => {
    if (!hasChildren) {
      return;
    }

    if (event.key === 'Escape' && collapsed) {
      event.preventDefault();
      setFlyoutOpen(false);
      return;
    }

    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleToggle();
    }
  };

  const enhancedChildren = childArray.map(child => {
    if (!React.isValidElement(child)) {
      return child;
    }

    const extraProps = {};

    if (collapsed) {
      extraProps.onNavigate = () => setFlyoutOpen(false);
    }

    return React.cloneElement(child, extraProps);
  });

  return (
    <div
      className={clsx('relative', collapsed && hasChildren && 'group')}
      onMouseEnter={() => collapsed && hasChildren && setFlyoutOpen(true)}
      onMouseLeave={() => collapsed && hasChildren && setFlyoutOpen(false)}
      onFocusCapture={() => collapsed && hasChildren && setFlyoutOpen(true)}
      onBlurCapture={event => {
        if (!collapsed || !hasChildren) {
          return;
        }
        if (!event.currentTarget.contains(event.relatedTarget)) {
          setFlyoutOpen(false);
        }
      }}
    >
      <button
        type="button"
        className={clsx(
          'sidebar-item sidebar-section-trigger group w-full min-w-0 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))]',
          collapsed ? 'min-h-[38px] justify-center px-2 py-2' : 'min-h-[38px] justify-between gap-2.5 px-2.5 py-1.5',
          isOpen && 'is-expanded'
        )}
        aria-expanded={hasChildren ? isOpen : undefined}
        aria-controls={hasChildren ? submenuId : undefined}
        aria-haspopup={hasChildren ? 'true' : undefined}
        aria-label={collapsed ? label : undefined}
        title={label}
        onClick={handleToggle}
        onKeyDown={handleKeyDown}
        data-has-active-child={active ? 'true' : 'false'}
      >
        <div className={clsx('flex min-w-0 flex-1 items-center gap-2.5', collapsed && 'justify-center')}>
          {Icon ? (
            <Icon
              aria-hidden="true"
              className={clsx(
                'sidebar-icon h-[18px] w-[18px] shrink-0 transition-colors duration-200',
                collapsed && 'mx-auto'
              )}
            />
          ) : null}
          {!collapsed && <span className="sidebar-item-label min-w-0 flex-1 text-[13px] font-semibold leading-4">{labelContent ?? label}</span>}
        </div>
        {!collapsed && hasChildren ? (
          <ChevronDown
            className={clsx(
              'ml-auto h-4 w-4 shrink-0 text-[rgba(var(--muted),0.9)] transition-transform duration-200',
              isOpen && 'rotate-180'
            )}
            aria-hidden="true"
          />
        ) : null}
      </button>

      {!collapsed && hasChildren && (
        <div
          id={submenuId}
          className={clsx('grid transition-[grid-template-rows] duration-200 ease-out', isOpen ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]')}
          role="group"
          aria-hidden={!isOpen}
        >
          <div className="overflow-hidden py-1 pl-3.5 pr-0.5 space-y-[3px]">
            {enhancedChildren}
          </div>
        </div>
      )}

      {collapsed && hasChildren ? (
        <div
          className={clsx(
            'absolute left-full top-0 ml-2 hidden w-60 max-h-[calc(100dvh-1rem)] overflow-y-auto rounded-2xl border border-[rgba(var(--border),0.6)] bg-[rgb(var(--panel))] p-2 shadow-[0_18px_40px_rgba(var(--shadow),0.18)]',
            (isOpen || active) && 'block',
            'group-hover:block group-focus-within:block',
            'dark:border-[rgba(var(--border),0.5)] dark:bg-[rgba(var(--panel),0.92)] dark:shadow-[0_18px_40px_rgba(var(--shadow),0.55)]'
          )}
          role="menu"
          aria-label={`Submenú ${label}`}
        >
          <div className="px-2 pb-1 text-xs font-semibold uppercase tracking-[0.35em] text-[rgba(var(--muted),0.75)]">
            {label}
          </div>
          <div className="space-y-1">{enhancedChildren}</div>
        </div>
      ) : null}
    </div>
  );
}

